import {
  ITableChange,
  TableBrowseSettings,
  TableRowValueList,
} from '@vinnova/ui';
import { BudgetTableChanges, EffektaBudgetAnswer } from '../budget.interface';

/**
 * Utility function to prevent non-integer input.
 * @param event - The keyboard event.
 */
export function preventNonIntegerInput(event: KeyboardEvent): void {
  if (event.key === '.' || event.key === ',') {
    event.preventDefault();
  }
}

export function getProjektpartChanges(
  budget: EffektaBudgetAnswer | undefined,
  projectPartId: string | undefined,
  isCost = false,
): ITableChange[] {
  const projectPart = budget?.projektparter?.find(
    (part) => part.id === projectPartId,
  );

  return isCost
    ? (projectPart?.kostnaderChanges ?? [])
    : (projectPart?.finansieringChanges ?? []);
}

/**
 * Utility function to calculate the difference in years between two dates.
 * @param startDate - The start date.
 * @param endDate - The end date.
 * @returns The difference in years.
 */
export function calculateYearDifference(
  startDate: Date | number,
  endDate: Date | number,
): number {
  const startYear =
    startDate instanceof Date ? startDate.getFullYear() : startDate;
  const endYear = endDate instanceof Date ? endDate.getFullYear() : endDate;
  return (endYear as number) - (startYear as number);
}

/**
 * Utility function to insert new table headers with changemarks.
 * @param tables - The tables to insert new headers into.
 * @param cols - The number of columns to insert.
 */
export function pushTableHeadersWithChanges(
  tables: BudgetTableChanges[],
  cols: number,
): void {
  for (const table of tables) {
    if (!table['head']) {
      table['head'] = [];
    }
    pushNewTableHeadersWithChangemark(table, cols);
  }
}

/**
 * Utility function to append new table headers with changemarks.
 * @param tables  - The tables to append new headers to.
 * @param startIndex - The index to start appending from.
 * @param cols - The number of columns to append.
 */
export function insertTableHeadersWithChanges(
  tables: BudgetTableChanges[],
  startIndex: number,
  cols: number,
): void {
  for (const table of tables) {
    if (!table['head']) {
      table['head'] = [];
    }
    insertNewTableHeadersWithChangemark(table, startIndex, cols);
  }
}

function pushNewTableHeadersWithChangemark(
  table: BudgetTableChanges,
  cols: number,
): void {
  for (let i = 0; i < cols; i++) {
    table['head'][i] = [{ new: true }];
  }
}

function insertNewTableHeadersWithChangemark(
  table: BudgetTableChanges,
  lastColumnIndex: number,
  cols: number,
): void {
  for (let i = lastColumnIndex; i < lastColumnIndex + cols; i++) {
    table['head'][i] = [{ new: true }];
  }
}

export function isEffektaSharedView() {
  const regex =
    /^https?:\/\/[^/]+\/projekt\/(budget|inrapporterade-kostnader)\/\d{4}-\d{5}(\/(write|read))?\/effekta(\/[^/]+)?$/;
  return regex.test(location.href);
}

/**
 * Utility function to set the year columns and browse settings in budget tables.
 * @param startYear - The project start year.
 * @param endYear - The project end year.
 * @returns The browse settings for small, medium and large screens, and the year columns.
 */
export function getBudgetTableColumns(
  startYear: number,
  endYear: number,
): TableBrowseSettings {
  const settings: TableBrowseSettings = {
    browseSmall: [],
    browseMedium: [],
    browseLarge: [],
    yearColumns: [],
  };

  if (startYear && endYear) {
    const columns = endYear - startYear + 1;

    if (columns <= 6) {
      settings.browseLarge.push({
        start: 0,
        length: columns,
      });
    } else {
      let columnsTakenLarge = 0;
      settings.browseLarge.push({
        start: columnsTakenLarge,
        length: 7,
      });
      columnsTakenLarge += 7;
      while (columnsTakenLarge <= columns) {
        columnsTakenLarge += 7;
        const lastPage = columns - columnsTakenLarge < 0;
        const secondLastPage = columns - columnsTakenLarge === 0;
        const start = lastPage
          ? columns - 6
          : secondLastPage
            ? columns - 7
            : columnsTakenLarge;
        settings.browseLarge.push({
          start: start,
          length: lastPage ? 6 : 7,
        });
      }
    }

    for (let i = startYear; i <= endYear; i++) {
      settings.yearColumns.push({
        id: (i - startYear).toString(),
        title: i.toString(),
      });

      settings.browseSmall.push({
        start: i - startYear,
        length: 1,
      });
    }

    let columnsTaken = columns % 2 === 0 ? 2 : 3;
    settings.browseMedium.push({
      start: columns - columnsTaken,
      length: columnsTaken,
    });
    while (columnsTaken < columns) {
      columnsTaken += 2;
      settings.browseMedium.unshift({
        start: columns - columnsTaken,
        length: 2,
      });
    }
  }
  return settings;
}

/**
 * Lägger till kolumnsummor, radsummor och stortotal till en värdelista.
 * Används för att förbereda data för ändringsmarkeringfiltrering.
 * @param values - Värdena att beräkna summor för.
 * @param yearColumns - Årskolumner för projektet.
 * @returns En ny lista med alla summor inkluderade.
 */
export function addRowAndColumnTotals(
  values: TableRowValueList,
  yearColumns: { id: string; title: string }[],
): TableRowValueList {
  if (!values || values.length === 0 || !yearColumns) {
    return values;
  }

  const result = [...values];

  for (let colIndex = 0; colIndex < yearColumns.length; colIndex++) {
    const colId = colIndex.toString();
    let totalForYear = 0;

    for (const item of values.filter((v) => v.columnId === colId)) {
      totalForYear += item.value || 0;
    }

    result.push({
      rowId: 'total',
      columnId: colId,
      value: totalForYear,
    });
  }

  const uniqueRowIds = [...new Set(values.map((v) => v.rowId))];
  for (const rowId of uniqueRowIds) {
    if (rowId !== 'total') {
      let rowTotal = 0;
      for (const item of values.filter((v) => v.rowId === rowId)) {
        rowTotal += item.value || 0;
      }
      result.push({
        rowId: rowId,
        columnId: 'total',
        value: rowTotal,
      });
    }
  }

  let grandTotal = 0;
  for (const item of values) {
    grandTotal += item.value || 0;
  }
  result.push({
    rowId: 'total',
    columnId: 'total',
    value: grandTotal,
  });

  return result;
}

/**
 * Hittar jämförelseversionen för en specifik cell bland ändringar från Effekta.
 * Den första versionen i den returnerade kedjan är antingen senaste positiva
 * beslut för BOÄ eller föregående inskickade version för ansökningskomplettering.
 */
export function findLatestDecision(
  changes: ITableChange[],
  rowId: string,
  columnId: string,
): ITableChange | undefined {
  return changes
    .filter(
      (c) =>
        c.rowId === rowId && c.columnId === columnId && c.order !== undefined,
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
}

/**
 * Slår samman ändringar från Effekta med användarändringar och filtrerar
 * bort de som matchar senaste beslutet.
 * @param effektaChanges - Ändringar från backend/Effekta.
 * @param existingChanges - Befintliga användarändringar.
 * @param parsedCurrentValues - Nuvarande värden i tabellen.
 * @param yearColumns - Årskolumner för projektet.
 * @returns Filtrerad lista med ändringar som ska visas.
 */
export function mergeEffektaAndUserChanges(
  effektaChanges: ITableChange[],
  existingChanges: ITableChange[],
  parsedCurrentValues: TableRowValueList,
  yearColumns: { id: string; title: string }[],
): ITableChange[] {
  const result: ITableChange[] = [];

  for (const change of effektaChanges) {
    const currentValue = parsedCurrentValues?.find(
      (v) =>
        v.rowId === change.rowId &&
        (v.columnId === 'total'
          ? change.columnId === 'total'
          : yearColumns[v.columnId]?.title === change.columnId),
    )?.value;

    const comparisonChange = findLatestDecision(
      effektaChanges,
      change.rowId,
      change.columnId,
    );

    if (
      currentValue === undefined ||
      !comparisonChange ||
      currentValue !== (comparisonChange.value ?? 0)
    ) {
      result.push(change);
    }
  }

  if (existingChanges) {
    for (const existingChange of existingChanges) {
      if (existingChange.order !== undefined) {
        continue;
      }

      const currentValue = parsedCurrentValues?.find(
        (value) =>
          value.rowId === existingChange.rowId &&
          (value.columnId === 'total'
            ? existingChange.columnId === 'total'
            : yearColumns[value.columnId]?.title === existingChange.columnId),
      )?.value;
      const comparisonChange = findLatestDecision(
        effektaChanges,
        existingChange.rowId,
        existingChange.columnId,
      );

      if (
        currentValue === undefined ||
        !comparisonChange ||
        currentValue !== (comparisonChange.value ?? 0)
      ) {
        result.push(existingChange);
      }
    }
  }

  return result;
}

export function removeLocalChanges(
  changes: ITableChange[],
  rowId: string,
  columnId: string,
): void {
  for (let index = changes.length - 1; index >= 0; index--) {
    const change = changes[index];
    if (
      change.rowId === rowId &&
      change.columnId === columnId &&
      change.order === undefined
    ) {
      changes.splice(index, 1);
    }
  }
}

export function removeRestoredTableChanges(
  tableChanges: BudgetTableChanges,
  incomingChanges: ITableChange[],
  currentValues: TableRowValueList,
  yearColumns: { id: string; title: string }[],
  rowIds: string[],
): void {
  const columnIds = [...yearColumns.map((column) => column.title), 'total'];

  for (const [rowIndex, rowId] of [...rowIds, 'total'].entries()) {
    for (const [columnIndex, columnId] of columnIds.entries()) {
      const comparisonChange = findLatestDecision(
        incomingChanges,
        rowId,
        columnId,
      );
      if (!comparisonChange) {
        continue;
      }

      const currentValue = currentValues.find(
        (value) =>
          value.rowId === rowId &&
          (value.columnId === 'total'
            ? columnId === 'total'
            : yearColumns[value.columnId]?.title === columnId),
      )?.value;

      if (
        currentValue !== undefined &&
        currentValue === (comparisonChange.value ?? 0) &&
        tableChanges[rowIndex]
      ) {
        tableChanges[rowIndex][columnIndex] = [];
      }
    }
  }
}

export function mergeIncomingChanges(
  incoming: ITableChange[],
  calculated: ITableChange[] | undefined,
): ITableChange[] {
  const uniqueIncoming = incoming.filter(
    (change, index, changes) =>
      index ===
      changes.findIndex(
        (candidate) =>
          candidate.rowId === change.rowId &&
          candidate.columnId === change.columnId &&
          candidate.order === change.order &&
          candidate.value === change.value &&
          candidate.text === change.text,
      ),
  );

  const localChanges = (calculated ?? []).filter(
    (change) =>
      !uniqueIncoming.some(
        (incomingChange) =>
          incomingChange.rowId === change.rowId &&
          incomingChange.columnId === change.columnId &&
          incomingChange.order === change.order &&
          incomingChange.value === change.value &&
          incomingChange.text === change.text,
      ),
  );

  const uniqueLocalChanges = localChanges.filter(
    (change, index, changes) =>
      index ===
      changes.findIndex(
        (candidate) =>
          candidate.rowId === change.rowId &&
          candidate.columnId === change.columnId &&
          candidate.value === change.value &&
          candidate.tooltipTextKey === change.tooltipTextKey &&
          candidate.text === change.text,
      ),
  );

  return [...uniqueIncoming, ...uniqueLocalChanges];
}
